import type { RouteRecordRaw } from 'vue-router'
import { PermissionBit } from '@/shared/constants/permissions.constants'

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/features/auth/pages/Login.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/auth/callback',
    name: 'oauth-callback',
    component: () => import('@/features/auth/pages/OAuthCallback.vue'),
    meta: { requiresAuth: false },
  },

  {
    path: '/',
    component: () => import('@/layouts/DefaultLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'home',
        component: () => import('@/features/system/pages/Home.vue'),
        meta: { title: 'Home' },
      },
      {
        path: 'permisos',
        name: 'my-permissions',
        component: () => import('@/features/system/pages/MyPermissions.vue'),
        meta: { title: 'Mis Permisos' },
      },

      {
        path: 'facturasemitidas',
        name: 'issued-invoices',
        component: () => import('@/features/billing/pages/IssuedInvoices.vue'),
        meta: { title: 'Facturas Emitidas', permission: PermissionBit.READ },
      },
      {
        path: 'mercadopago/liberaciones',
        name: 'mercadopago-liberaciones',
        component: () => import('@/features/billing/pages/MercadopagoLiberaciones.vue'),
        meta: { title: 'Liberaciones MP', permission: PermissionBit.READ },
      },
      {
        path: 'notascobro/creacion',
        name: 'create-collection-note',
        component: () => import('@/features/billing/pages/CreateCollectionNote.vue'),
        meta: { title: 'Crear Nota de Cobro', permission: PermissionBit.CREATE },
      },
      {
        path: 'notascobro/edicion',
        name: 'edit-collection-notes',
        component: () => import('@/features/billing/pages/EditCollectionNotes.vue'),
        meta: { title: 'Editar Notas de Cobro', permission: PermissionBit.UPDATE },
      },
      {
        path: 'facturas/creacion',
        name: 'create-invoice',
        component: () => import('@/features/billing/pages/CreateInvoice.vue'),
        meta: { title: 'Crear Factura', permission: PermissionBit.CREATE },
      },
      {
        path: 'facturas/subir-masivo',
        name: 'bulk-paid-invoice-upload',
        component: () => import('@/features/billing/pages/BulkPaidInvoiceUpload.vue'),
        meta: { title: 'Subir Facturas Masivo', permission: PermissionBit.CREATE },
      },
      {
        path: 'facturas/siigo-mensual',
        name: 'siigo-monthly-export',
        component: () => import('@/features/billing/pages/SiigoMonthlyExport.vue'),
        meta: { title: 'Facturas SIIGO Mensuales', permission: PermissionBit.READ },
      },
      {
        path: 'facturas/implementacion',
        name: 'implementation-invoices',
        component: () => import('@/features/billing/pages/ImplementationInvoices.vue'),
        meta: { title: 'Facturas de Implementación', permission: PermissionBit.READ },
      },
      {
        path: 'fechasfacturacion',
        name: 'billing-dates',
        component: () => import('@/features/billing/pages/BillingDates.vue'),
        meta: { title: 'Fechas de Facturación', permission: PermissionBit.UPDATE },
      },
      {
        path: 'cobromanual',
        name: 'manual-charge',
        component: () => import('@/features/billing/pages/ManualCharge.vue'),
        meta: { title: 'Cobro Manual', permission: PermissionBit.CREATE },
      },
      {
        path: 'analitica-billing',
        name: 'billing-analytics',
        component: () => import('@/features/billing-analytics/pages/BillingAnalyticsPage.vue'),
        meta: { title: 'Análisis de Billing', permission: PermissionBit.READ },
      },
      {
        path: 'conciliacion-pagos',
        name: 'payment-reconciliation',
        component: () =>
          import('@/features/payment-reconciliation/pages/PaymentReconciliation.vue'),
        meta: { title: 'Conciliación de Pagos', permission: PermissionBit.UPDATE },
      },

      {
        path: 'restaurantes',
        name: 'restaurants',
        component: () => import('@/features/restaurants/pages/RestaurantList.vue'),
        meta: { title: 'Restaurantes', permission: PermissionBit.READ },
      },
      {
        path: 'restaurantesoperando',
        name: 'restaurants-operating',
        component: () => import('@/features/restaurants/pages/RestaurantsOperating.vue'),
        meta: { title: 'Restaurantes Operando', permission: PermissionBit.READ },
      },
      {
        path: 'restaurantesoperando/:idResto/:idLocal',
        name: 'restaurant-detail',
        component: () => import('@/features/restaurants/pages/RestaurantDetail.vue'),
        meta: { title: 'Detalle de Restaurante', permission: PermissionBit.READ },
      },
      {
        path: 'maparestaurantes',
        name: 'restaurant-map',
        component: () => import('@/features/restaurants/pages/RestaurantMapView.vue'),
        meta: { title: 'Mapa de Restaurantes', permission: PermissionBit.READ },
      },
      {
        path: 'restaurantespendientes',
        name: 'pending-restaurants',
        component: () => import('@/features/restaurants/pages/PendingRestaurants.vue'),
        meta: { title: 'Restaurantes Pendientes', permission: PermissionBit.READ },
      },
      {
        path: 'agregalocal',
        name: 'add-local',
        component: () => import('@/features/restaurants/pages/AddLocal.vue'),
        meta: { title: 'Agregar Local', permission: PermissionBit.CREATE },
      },
      {
        path: 'cambianombrelocal',
        name: 'change-local-name',
        component: () => import('@/features/restaurants/pages/ChangeLocalName.vue'),
        meta: { title: 'Cambiar Nombre Local', permission: PermissionBit.UPDATE },
      },
      {
        path: 'facturacionmensual',
        name: 'monthly-billing-list',
        component: () => import('@/features/restaurants/pages/MonthlyBillingList.vue'),
        meta: { title: 'Facturacion Mensual', permission: PermissionBit.READ },
      },
      {
        path: 'alertachurn',
        name: 'churn-warning',
        component: () => import('@/features/restaurants/pages/ChurnWarningList.vue'),
        meta: { title: 'Alerta de Churn', permission: PermissionBit.READ },
      },
      {
        path: 'creditos',
        name: 'credit-accounts',
        component: () => import('@/features/restaurants/pages/CreditAccountList.vue'),
        meta: { title: 'Créditos', permission: PermissionBit.READ },
      },
      {
        path: 'creditos/:idResto/:idLocal',
        name: 'credit-account-detail',
        component: () => import('@/features/restaurants/pages/CreditAccountDetail.vue'),
        meta: { title: 'Detalle de Créditos', permission: PermissionBit.READ },
      },

      {
        path: 'ordenes/rechazadas',
        name: 'rejected-orders',
        component: () => import('@/features/orders/pages/RejectedOrders.vue'),
        meta: { title: 'Órdenes Rechazadas', permission: PermissionBit.READ },
      },

      {
        path: 'ubereats',
        name: 'ubereats',
        component: () => import('@/features/marketplace/pages/UberEats.vue'),
        meta: { title: 'Uber Eats', permission: PermissionBit.READ },
      },
      {
        path: 'rappi',
        name: 'rappi',
        component: () => import('@/features/marketplace/pages/Rappi.vue'),
        meta: { title: 'Rappi', permission: PermissionBit.READ },
      },
      {
        path: 'pedidosya',
        name: 'pedidosya',
        component: () => import('@/features/marketplace/pages/PedidosYa.vue'),
        meta: { title: 'PedidosYa', permission: PermissionBit.READ },
      },
      {
        path: 'justo',
        name: 'justo',
        component: () => import('@/features/marketplace/pages/Justo.vue'),
        meta: { title: 'Justo', permission: PermissionBit.READ },
      },
      {
        path: 'justo/reset',
        name: 'justo-reset',
        component: () => import('@/features/marketplace/pages/JustoReset.vue'),
        meta: { title: 'Resetear Menu Justo', permission: PermissionBit.UPDATE },
      },
      {
        path: 'menusync',
        name: 'menu-sync',
        component: () => import('@/features/marketplace/pages/MenuSync.vue'),
        meta: { title: 'Sincronización de Menú', permission: PermissionBit.READ },
      },

      {
        path: 'timezonechange',
        name: 'timezone-change',
        component: () => import('@/features/system/pages/TimezoneChange.vue'),
        meta: { title: 'Cambio de Zona Horaria', permission: PermissionBit.UPDATE },
      },
      {
        path: 'datastore',
        name: 'datastore-viewer',
        component: () => import('@/features/system/pages/DatastoreViewer.vue'),
        meta: { title: 'Visor Datastore', permission: PermissionBit.READ },
      },

      {
        path: 'portales',
        name: 'portals',
        component: () => import('@/features/portals/pages/Portals.vue'),
        meta: { title: 'Portales', permission: PermissionBit.READ },
      },

      {
        path: 'roles',
        name: 'profiles-list',
        component: () => import('@/features/profiles/pages/ProfileList.vue'),
        meta: { title: 'Configuración de Roles', permission: PermissionBit.READ },
      },
      {
        path: 'roles/:profileId',
        name: 'profiles-detail',
        component: () => import('@/features/profiles/pages/ProfileDetail.vue'),
        meta: { title: 'Editar Perfil', permission: PermissionBit.UPDATE },
      },
      {
        path: 'modulos',
        name: 'modules-list',
        component: () => import('@/features/modules/pages/ModulesList.vue'),
        meta: { title: 'Módulos', permission: PermissionBit.READ },
      },
      {
        path: 'modulos/agregar',
        name: 'modules-add',
        component: () => import('@/features/modules/pages/ModuleForm.vue'),
        meta: { title: 'Nuevo Módulo', permission: PermissionBit.CREATE },
      },
      {
        path: 'modulos/editar/:moduleId',
        name: 'modules-edit',
        component: () => import('@/features/modules/pages/ModuleForm.vue'),
        meta: { title: 'Editar Módulo', permission: PermissionBit.UPDATE },
      },
      {
        path: 'modulos/perfiles/:mod',
        name: 'module-profiles',
        component: () => import('@/features/profiles/pages/ModuleProfiles.vue'),
        meta: { title: 'Accesos a Módulo', permission: PermissionBit.READ },
      },

      {
        path: 'usuarios',
        name: 'users-list',
        component: () => import('@/features/users/pages/UsersList.vue'),
        meta: { title: 'Usuarios', permission: PermissionBit.READ },
      },
      {
        path: 'usuarios/editar/:id',
        name: 'user-edit',
        component: () => import('@/features/users/pages/UserEdit.vue'),
        meta: { title: 'Editar Usuario', permission: PermissionBit.UPDATE },
      },
      {
        path: 'usuarios/perfiles/:id',
        name: 'user-permissions',
        component: () => import('@/features/users/pages/UserPermissions.vue'),
        meta: { title: 'Permisos de Usuario', permission: PermissionBit.READ },
      },
      {
        path: 'usuarios/sesiones/:id',
        name: 'user-sessions',
        component: () => import('@/features/users/pages/UserSessions.vue'),
        meta: { title: 'Sesiones de Usuario', permission: PermissionBit.READ },
      },
      {
        path: 'usuarios/monitor/:id',
        name: 'user-monitor',
        component: () => import('@/features/users/pages/UserMonitor.vue'),
        meta: { title: 'Monitor de Usuario', permission: PermissionBit.READ },
      },

      {
        path: 'payments',
        name: 'payments-dashboard',
        component: () => import('@/features/payments/pages/PaymentsDashboard.vue'),
        meta: { title: 'Dashboard de Pagos', permission: PermissionBit.READ },
      },
      {
        path: 'formasdepago',
        name: 'payment-methods',
        component: () => import('@/features/payment-methods/pages/PaymentMethodsList.vue'),
        meta: { title: 'Formas de Pago', permission: PermissionBit.READ },
      },
      {
        path: 'corrigeordenes',
        name: 'corregir-ordenes',
        component: () => import('@/features/admin-tools/pages/AdminToolPage.vue'),
        meta: {
          title: 'Corregir Turno de Ordenes',
          toolId: 'corregir-ordenes',
          permission: PermissionBit.UPDATE,
        },
      },
      {
        path: 'corrigecajaordenes',
        name: 'corregir-caja-ordenes',
        component: () => import('@/features/admin-tools/pages/AdminToolPage.vue'),
        meta: {
          title: 'Corregir Caja de Ordenes',
          toolId: 'corregir-caja-ordenes',
          permission: PermissionBit.UPDATE,
        },
      },
      {
        path: 'reseteapagos',
        name: 'resetear-pagos',
        component: () => import('@/features/admin-tools/pages/AdminToolPage.vue'),
        meta: {
          title: 'Resetear Pagos',
          toolId: 'resetear-pagos',
          permission: PermissionBit.UPDATE,
        },
      },
      {
        path: 'eliminaorden',
        name: 'eliminar-orden',
        component: () => import('@/features/admin-tools/pages/AdminToolPage.vue'),
        meta: {
          title: 'Eliminar Orden',
          toolId: 'eliminar-orden',
          permission: PermissionBit.DELETE,
        },
      },

      {
        path: 'droneversiones',
        name: 'drone-versions',
        component: () => import('@/features/drone-versions/pages/DroneVersions.vue'),
        meta: { title: 'Versiones de Drone', permission: PermissionBit.READ },
      },
      {
        path: 'intercomConversations',
        name: 'intercom-conversations',
        component: () => import('@/features/intercom/pages/IntercomConversations.vue'),
        meta: { title: 'Conversaciones Intercom', permission: PermissionBit.READ },
      },
      {
        path: 'facturacion',
        name: 'facturacion-config',
        component: () => import('@/features/facturacion/pages/FacturacionConfigs.vue'),
        meta: { title: 'Configuración de Facturación', permission: PermissionBit.UPDATE },
      },
      {
        path: 'multisucursal/reintentar',
        name: 'retry-operations',
        component: () => import('@/features/brand-menus/pages/RetryOperations.vue'),
        meta: {
          title: 'Re-intentar Operaciones',
          permission: [PermissionBit.SPECIAL, PermissionBit.UPDATE],
        },
      },
      {
        path: 'multisucursal/sincronizar-templates',
        name: 'reset-templates',
        component: () => import('@/features/brand-menus/pages/ResetTemplates.vue'),
        meta: {
          title: 'Sincronizar Templates',
          permission: [PermissionBit.SPECIAL, PermissionBit.UPDATE],
        },
      },

      {
        path: 'inventory/regeneraMovInv',
        name: 'regenerar-movimientos-inventario',
        component: () => import('@/features/admin-tools/pages/AdminToolPage.vue'),
        meta: {
          title: 'Regenerar Movimientos Inventario',
          toolId: 'regenerar-movimientos-inventario',
          permission: PermissionBit.UPDATE,
        },
      },
      {
        path: 'regeneracierre',
        name: 'regenerar-cierre',
        component: () => import('@/features/admin-tools/pages/AdminToolPage.vue'),
        meta: {
          title: 'Regenerar Cierre',
          toolId: 'regenerar-cierre',
          permission: PermissionBit.UPDATE,
        },
      },
      {
        path: 'traducciones',
        name: 'translations-list',
        component: () => import('@/features/translations/pages/TranslationsList.vue'),
        meta: { title: 'Traducciones', permission: PermissionBit.READ },
      },
      {
        path: 'traducciones/agregar',
        name: 'translations-add',
        component: () => import('@/features/translations/pages/TranslationsAdd.vue'),
        meta: { title: 'Nueva Traducción', permission: PermissionBit.CREATE },
      },
      {
        path: 'traducciones/editar/:moduleId/:tradId',
        name: 'translations-edit',
        component: () => import('@/features/translations/pages/TranslationsEdit.vue'),
        meta: { title: 'Editar Traducción', permission: PermissionBit.READ },
      },
    ],
  },

  {
    path: '/unauthorized',
    name: 'unauthorized',
    component: () => import('@/features/system/pages/Unauthorized.vue'),
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/features/system/pages/NotFound.vue'),
  },
]

export default routes
